import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, TrendingUp, MapPin, Users } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";

export default function SEOOrlandoPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "SEO en Orlando",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Orlando",
      "addressRegion": "FL"
    },
    "description": "Servicios profesionales de SEO Local en Orlando, Florida. Posicionamiento orgánico para negocios hispanos con estrategias medibles y generación de leads."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Cuánto tiempo toma ver resultados de SEO en Orlando?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Típicamente 8-12 semanas para mejoras iniciales en rankings locales. Resultados sostenidos y significativos en 3-6 meses con estrategia consistente."
        }
      },
      {
        "@type": "Question",
        "name": "¿Qué incluye el SEO local para Orlando?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Optimización de Google Business Profile, páginas específicas por zona (Winter Park, Lake Nona, etc.), contenido localizado, link building con medios de Orlando, y tracking de llamadas y formularios."
        }
      },
      {
        "@type": "Question",
        "name": "¿Trabajan con negocios hispanos en Orlando?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sí, somos especialistas en mercado hispano. Optimizamos en español e inglés, conocemos las particularidades culturales y áreas de alta concentración latina en Orlando."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Servicios",
        "item": "https://luisroc.com/services"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Florida",
        "item": "https://luisroc.com/servicios-locales-florida"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "SEO Orlando",
        "item": "https://luisroc.com/seo-orlando"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <html lang="es" />
        <title>Agencia SEO en Orlando, FL | Posicionamiento Local y Leads – Luis ROC</title>
        <meta name="description" content="SEO Local en Orlando, Florida. Aumenta tu visibilidad en Google para negocios hispanos. Reunión inicial gratuita, estrategia medible y generación de leads reales." />
        <link rel="canonical" href="https://luisroc.com/seo-orlando" />
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando, Florida" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://luisroc.com/seo-orlando" />
        <meta property="og:title" content="Agencia SEO en Orlando, FL – Luis ROC" />
        <meta property="og:description" content="SEO Local en Orlando. Estrategia medible, auditoría técnica y generación de leads para negocios hispanos." />
        <meta property="og:locale" content="es_US" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SEO en Orlando, FL – Luis ROC" />
        <meta name="twitter:description" content="Posicionamiento local en Orlando con resultados medibles." />
      </Helmet>

      <Header language="es" onLanguageChange={() => {}} />
      
      <main className="min-h-screen bg-background">
        <BreadcrumbNav 
          items={[
            { name: "Servicios", url: "/servicios" },
            { name: "Florida", url: "/servicios-locales-florida" },
            { name: "SEO Orlando", url: "/seo-orlando" }
          ]}
          language="es"
        />

        <section className="bg-gradient-to-br from-primary/10 via-background to-background py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin className="w-6 h-6 text-primary" />
                <span className="text-lg font-semibold text-primary">Orlando, Florida</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Agencia SEO en Orlando que Convierte Búsquedas en Clientes
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Posicionamiento orgánico para negocios hispanos en Orlando. SEO local, contenido optimizado y tracking completo de leads.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <a href="/contacto">
                    Agenda tu reunión inicial gratuita de 30 minutos
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="tel:+18325215050">Llama Ahora</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 text-foreground">
                  Qué Incluye Nuestro SEO Local en Orlando
                </h2>
                <p className="text-lg text-muted-foreground">
                  Estrategia completa para dominar las búsquedas locales en Orlando y área metropolitana
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Auditoría Técnica SEO</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Análisis completo de tu sitio: velocidad, Core Web Vitals, indexación, errores técnicos y optimización mobile.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Google Business Profile</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Optimización completa de perfil, posts semanales, gestión de reseñas y estrategia de Local Pack.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Contenido Localizado</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Páginas por zona (Winter Park, Lake Nona, Dr. Phillips), blogs bilingües y contenido de valor para tu audiencia.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Link Building Local</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Enlaces de calidad desde medios locales de Orlando, directorios hispanos y asociaciones de negocios.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Tracking de Conversiones</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Seguimiento completo de llamadas, formularios y conversiones con panel mensual de ROI.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Reportes Mensuales</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Informes claros con rankings, tráfico orgánico, leads generados y recomendaciones del próximo mes.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
                Preguntas Frecuentes sobre SEO en Orlando
              </h2>
              
              <div className="space-y-6">
                <details className="group bg-background rounded-lg p-6 shadow-sm">
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-lg">
                    ¿Cuánto tiempo toma ver resultados de SEO en Orlando?
                    <span className="text-primary transition group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">
                    Típicamente 8-12 semanas para mejoras iniciales en rankings locales. Resultados sostenidos y significativos en 3-6 meses con estrategia consistente.
                  </p>
                </details>

                <details className="group bg-background rounded-lg p-6 shadow-sm">
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-lg">
                    ¿Qué incluye el SEO local para Orlando?
                    <span className="text-primary transition group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">
                    Optimización de Google Business Profile, páginas específicas por zona (Winter Park, Lake Nona, etc.), contenido localizado, link building con medios de Orlando, y tracking de llamadas y formularios.
                  </p>
                </details>

                <details className="group bg-background rounded-lg p-6 shadow-sm">
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-lg">
                    ¿Trabajan con negocios hispanos en Orlando?
                    <span className="text-primary transition group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">
                    Sí, somos especialistas en mercado hispano. Optimizamos en español e inglés, conocemos las particularidades culturales y áreas de alta concentración latina en Orlando.
                  </p>
                </details>

                <details className="group bg-background rounded-lg p-6 shadow-sm">
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-lg">
                    ¿Cómo miden el ROI del SEO?
                    <span className="text-primary transition group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">
                    Configuramos tracking completo: leads por canal (orgánico/Google Business), grabación de llamadas, formularios, y panel mensual con CPA y valor de cada conversión.
                  </p>
                </details>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <TrendingUp className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4 text-foreground">
                ¿Listo para Dominar las Búsquedas en Orlando?
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Agenda una reunión inicial gratuita y descubre cómo podemos aumentar tu visibilidad en Google.
              </p>
              <Button asChild size="lg" className="text-lg">
                <a href="/contacto">
                  Agenda tu reunión inicial gratuita de 30 minutos
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

        <KnowledgeConnections path="/seo-orlando" language="es" />

      <Footer language="es" />
      <JsonLd data={serviceSchema} />
      <JsonLd data={faqSchema} />
      <JsonLd data={breadcrumbSchema} />
    </>
  );
}
